import styled, { css, keyframes } from 'styled-components';
import mainTheme from '../mainTheme';
import { mediaHover } from '../utils';
const getUnit = ({ value, basedOnRem }) => {
    // eslint-disable-next-line prefer-const
    let [, number, unit] = String(value).match(/(\d+)([a-zA-Z]+)?/i) || [];
    if (!number)
        return;
    unit || (unit = basedOnRem ? 'rem' : 'px');
    return number + unit;
};
const defaultFontSize = { xl: '14px', l: '14px', m: '13px', sm: '10px' };
const defaultPadding = { xl: [11, 24], l: [11, 24], m: [6, 15], sm: [5, 12] };
const defaultMinWidth = { xl: '120px', l: '120px', m: 'auto', sm: 'auto' };
const breakpointFontSize = { xl: '15px', l: '12px', m: '13px', sm: '11px' };
const breakpointPadding = { xl: [13, 28], l: [8, 19], m: [6, 15], sm: [6, 14] };
const breakpointMinWidth = { xl: '120px', l: '100px', m: 'auto', sm: 'auto' };
const borderSize = 2;
const getPadding = ({ source, hardCorners, height, outlineAccent }) => {
    let [topBottom, rightLeft] = source;
    hardCorners && (rightLeft -= 3);
    if (outlineAccent) {
        rightLeft -= borderSize;
        topBottom -= borderSize;
    }
    return `padding: ${height ? 0 : `${topBottom}px`} ${rightLeft}px`;
};
const getLoadingSize = (size) => {
    return css `
    width: ${defaultPadding[size][0] + defaultPadding[size][1]}px;
    height: ${defaultPadding[size][0] + defaultPadding[size][1]}px;

    @media (max-width: 790px) {
      width: ${breakpointPadding[size][0] + breakpointPadding[size][1]}px;
      height: ${breakpointPadding[size][0] + breakpointPadding[size][1]}px;
    }
  `;
};
const loadingAnimation = keyframes `
  to {
    transform: rotate(1turn);
  }
`;
const fadeIn = keyframes `
  to {
    opacity: 1;
  }
`;
/**
 * This is main Alicorn Button
 * @param {string} [size='l'] - Button size (`xl`, `l`, `m`, `sm`). `l` is default
 * @param {boolean} [outlineAccent=] - `boolean`
 * @param {boolean} [noBackground=] - `boolean`
 * @param {boolean} [hardCorners=] - `boolean`
 * @param {boolean} [fullWidth=] - `boolean`
 * @param {number|string} [fullWidthOn=] - button will be full width when screen width is less then... `number|string`
 * @param {number|string} [height=] - optional button height if default is not good in current case `number|string`
 * @param {boolean} [isWhite=] - specific white button style `boolean`
 * @param {boolean} [isGray=] - specific gray button style `boolean`
 * @param {boolean} [isLoading=] - enable loading state `boolean`
 * @param {boolean} [basedOnRem=] - should be used only if `rem` is adaptive to screen width `boolean`
 * @param {number|string} [mt=] - margin-top `number|string`
 * @param {number|string} [mb=] - margin-bottom `number|string`
 * @param {number|string} [ml=] - margin-left `number|string`
 * @param {number|string} [mr=] - margin-right `number|string`
 */
export default styled.button `
  ${({ size = 'l', outlineAccent, noBackground, hardCorners, fullWidth, fullWidthOn, height, isWhite, isGray, isLoading, isError, isSuccess, basedOnRem, mt, mb, ml, mr, }) => css `
    ${basedOnRem
    ? css `
          min-width: 13rem;
          font-size: 1.6rem;
          padding: 1.1rem 2.4rem;
        `
    : css `
          min-width: ${defaultMinWidth[size]};
          font-size: ${defaultFontSize[size]};
          ${getPadding({ source: defaultPadding[size], hardCorners, height, outlineAccent })};

          @media (max-width: 790px) {
            min-width: ${breakpointMinWidth[size]};
            font-size: ${breakpointFontSize[size]};
            ${getPadding({ source: breakpointPadding[size], hardCorners, height, outlineAccent })};
          }
        `}

    border-radius: ${hardCorners ? 10 : 100}px;
    border: ${outlineAccent
    ? `${borderSize}px solid ${isWhite ? '#fff' : isGray ? mainTheme.textColorSecondary : mainTheme.colorAccent1}`
    : 0};
    outline: 0;
    font-family: DINPro, sans-serif;
    font-weight: 700;
    line-height: 1.6;
    letter-spacing: 0.6px;
    color: #fff;
    text-transform: uppercase;
    cursor: pointer;
    transition: 0.2s;
    white-space: nowrap;

    ${height && `height: ${getUnit({ value: height, basedOnRem })}`};

    ${fullWidth &&
    css `
      width: 100%;
      display: block;
    `}

    ${mt && `margin-top: ${getUnit({ value: mt, basedOnRem })}`};
    ${mr && `margin-right: ${getUnit({ value: mr, basedOnRem })}`};
    ${ml && `margin-left: ${getUnit({ value: ml, basedOnRem })}`};
    ${mb && `margin-bottom: ${getUnit({ value: mb, basedOnRem })}`};

    ${noBackground
    ? css `
          background: none;
          ${isGray && `color: ${mainTheme.textColor3}`};
        `
    : css `
          ${isWhite
        ? css `
                color: ${mainTheme.colorAccent2};
                background: #fff;
              `
        : css `
                background: ${isError ? '#8b572a' : outlineAccent ? mainTheme.colorAccent2 : mainTheme.colorAccent1};
              `}
        `}

    :not(:disabled) {
      ${isLoading
    ? css `
            display: inline-grid;
            place-items: center;
            visibility: hidden;
            position: relative;

            &::after {
              content: '';

              position: absolute;
              visibility: visible;
              opacity: 0;

              border: ${['m', 'sm'].includes(size) ? 3 : 4}px solid ${mainTheme.colorAccent1};
              border-top-color: #fff;
              border-radius: 50%;
              animation: ${loadingAnimation} 1s ease infinite, ${fadeIn} 0.4s forwards;
              animation-delay: 0ms, 100ms;

              ${getLoadingSize(size)};
            }
          `
    : mediaHover(!isError && !isSuccess
        ? css `
                  filter: brightness(${isGray ? 1 : isWhite ? 0.95 : 0.8});

                  ${isGray &&
            css `
                    border-color: #fff;
                    color: #fff;
                  `}
                `
        : '')}
    }

    :disabled {
      ${outlineAccent ? `border-color: ${mainTheme.backgroundColor1}` : `background: ${mainTheme.backgroundColor1}`};

      color: ${mainTheme.disabledTextColor1};
      cursor: not-allowed;
    }

    ${fullWidthOn &&
    css `
      @media (max-width: ${getUnit({ value: fullWidthOn, basedOnRem })}) {
        width: 100%;
        display: block;
      }
    `}
  `}
`;
//# sourceMappingURL=index.js.map